import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Input, Modal, ConfirmDialog, EmptyState } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { usersAPI, activityAPI } from '../api/client';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'admin', label: 'Administrador', desc: 'Acceso total incluyendo usuarios y configuración' },
  { value: 'gerente', label: 'Gerente', desc: 'Puede consultar métricas del CRM por WhatsApp' },
  { value: 'operator', label: 'Operador', desc: 'Puede publicar y ver analytics, no puede configurar' }
];

const EMPTY_FORM = { name: '', email: '', role: 'operator', password: '', is_active: true };

function UserForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial ? { ...initial, password: '' } : EMPTY_FORM);
  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!form.name || !form.email) { toast.error('Nombre y email requeridos'); return; }
      if (!initial && !form.password) { toast.error('Contraseña requerida'); return; }
      onSave(form);
    }} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Input label="Nombre completo" value={form.name} onChange={set('name')} placeholder="Ej: Juan Pérez" required icon="👤" />
      <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="juan@empresa.com" required icon="✉" />
      <Input label={initial ? 'Nueva contraseña (dejar vacío para mantener)' : 'Contraseña'} type="password" value={form.password} onChange={set('password')} placeholder="Mínimo 8 caracteres" required={!initial} icon="🔒" />

      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Rol</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {ROLES.map(r => (
            <label key={r.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', borderRadius: 'var(--radius-md)', border: `1px solid ${form.role === r.value ? '#7c3aed60' : 'var(--border)'}`, background: form.role === r.value ? '#7c3aed10' : 'var(--bg-elevated)', cursor: 'pointer' }}>
              <input type="radio" name="role" value={r.value} checked={form.role === r.value} onChange={() => set('role')(r.value)} style={{ marginTop: '2px', accentColor: '#7c3aed' }} />
              <div>
                <div style={{ fontWeight: '600', fontSize: '13px' }}>{r.label}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{r.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px' }}>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={loading}>{initial ? 'Guardar cambios' : 'Crear usuario'}</Button>
      </div>
    </form>
  );
}

export default function UsersPage() {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [activityLog, setActivityLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchLog = async () => {
    setLoadingLog(true);
    try { const { data } = await activityAPI.list(100); setActivityLog(data); }
    catch {} finally { setLoadingLog(false); }
  };

  useEffect(() => { if (activeTab === 'activity') fetchLog(); }, [activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await usersAPI.list();
      setUsers(Array.isArray(data) ? data : data.items || []);
    } catch {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return (
    <EmptyState icon="🔒" title="Sin acceso" description="Solo los administradores pueden gestionar usuarios." />
  );

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await usersAPI.update(editing.id, payload);
        toast.success('Usuario actualizado ✓');
      } else {
        await usersAPI.create({ name: form.name, email: form.email, role: form.role, password: form.password, is_active: form.is_active });
        toast.success('Usuario creado ✓');
      }
      await fetchUsers();
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await usersAPI.delete(id);
      toast.success('Usuario eliminado');
      await fetchUsers();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async (u) => {
    try {
      await usersAPI.update(u.id, { is_active: !u.is_active });
      await fetchUsers();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const ROLE_BADGE = {
    admin:    { color: '#f59e0b', label: 'Admin' },
    gerente:  { color: '#10b981', label: 'Gerente' },
    operator: { color: '#06b6d4', label: 'Operador' },
  };

  const ACTION_ICON = { publish: '📤', delete: '🗑', login: '🔑', create: '➕', update: '✏️', retry: '🔄' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', alignSelf: 'flex-start' }}>
        {[{ id: 'users', label: '👥 Usuarios' }, { id: 'activity', label: '📋 Log de actividad' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: activeTab === t.id ? 'var(--bg-card)' : 'transparent', color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: activeTab === t.id ? '700' : '500', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'activity' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {loadingLog ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Cargando log...</p>
          ) : activityLog.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', textAlign: 'center', padding: '32px' }}>Sin actividad registrada</p>
          ) : activityLog.map((log, i) => (
            <div key={log.id} className={`animate-fadeIn delay-${Math.min(i+1,5)}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>{ACTION_ICON[log.action] || '📝'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>{log.detail || log.action}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {log.user_name || 'Sistema'} · {new Date(log.created_at).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
              {log.entity && <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{log.entity}</span>}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'users' && <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{users.length} usuarios registrados</p>
        <Button icon="+" onClick={() => { setEditing(null); setShowForm(true); }}>Nuevo usuario</Button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Cargando usuarios...</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {users.map((u, i) => (
            <Card key={u.id} className={`animate-fadeIn delay-${i + 1}`} style={{ padding: '16px 20px', opacity: u.is_active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>
                  {u.name.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', fontSize: '14px' }}>{u.name}</span>
                    {u.id === currentUser?.id && <Badge color="#7c3aed">Tú</Badge>}
                    <Badge color={ROLE_BADGE[u.role]?.color || '#06b6d4'}>{ROLE_BADGE[u.role]?.label || u.role}</Badge>
                    {!u.is_active && <Badge color="#ef4444">Inactivo</Badge>}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>
                    {u.email} · Desde {u.created_at ? u.created_at.split('T')[0] : '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button onClick={() => toggleActive(u)} style={{ background: u.is_active ? '#10b98115' : '#ef444415', border: `1px solid ${u.is_active ? '#10b98140' : '#ef444440'}`, color: u.is_active ? '#10b981' : '#ef4444', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontFamily: 'var(--font-body)', fontWeight: '600' }}>
                    {u.is_active ? 'Activo' : 'Inactivo'}
                  </button>
                  <button onClick={() => { setEditing(u); setShowForm(true); }} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', fontSize: '13px' }}>✏️</button>
                  <button onClick={() => setDeleting(u)} disabled={u.id === currentUser?.id} style={{ background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444', cursor: u.id === currentUser?.id ? 'not-allowed' : 'pointer', padding: '6px 10px', borderRadius: '8px', fontSize: '13px', opacity: u.id === currentUser?.id ? 0.4 : 1 }}>🗑</button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      </>}

      <Modal open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? 'Editar usuario' : 'Nuevo usuario'} width={480}>
        <UserForm initial={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} loading={saving} />
      </Modal>

      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={() => { handleDelete(deleting?.id); setDeleting(null); }} title="Eliminar usuario" message={`¿Eliminar a "${deleting?.name}"? Esta acción no se puede deshacer.`} confirmLabel="Eliminar" danger />
    </div>
  );
}
